import {
  addCancelledOrderToCache,
  addCreatedOrders,
  refreshOrderDerivedFields,
  updateOrderStatus,
} from "../store/actions/ordersSlice";
import store from "../store/store";
import { ORDER_STATUS_CANCELLED } from "../types/order";
import { updateShippingRoutes } from "../store/actions/shippingRoutesSlice";
import { dispatchAddAlert } from "../utils/alerts";
import { getOrdersV2 } from "./order";

// The `orders_created` payload is built when the order row is inserted, before
// its payments and products are, so has_card / has_drink / order_products are
// always empty there. Without this the dispatch modal never asks for a
// maquininha on orders that arrived live (only after a full page reload).
// Re-read the order through the dashboard list — the same serializer a reload
// uses; `?oid=` returns a legacy shape without these fields.
const refreshCreatedOrder = (order) => {
  if (!order?.order_id || order.order_index == null) return;

  getOrdersV2({ get_all: true, query: order.order_index })
    .then(({ data }) => {
      const fresh = Array.isArray(data)
        ? data.find((o) => o.order_id === order.order_id)
        : null;
      if (fresh) {
        store.dispatch(refreshOrderDerivedFields(fresh));
      }
    })
    .catch(() => {});
};

let socket = null;
let messageListener = null; // Store a message listener function
const maxRetries = 3; // Maximum number of retry attempts
let retryCount = 0; // Track retry attempts
let lastHeartbeatTime = 0; // Track time of last received heartbeat
let heartbeatCheckInterval = null; // Interval to check for missed heartbeats
let heartbeatCount = 0; // Count heartbeats for logging purposes
let connectionInProgress = false; // Flag to track connection in progress
let connectionId = 0; // Unique ID for each connection attempt

// Debug function for tracking connection lifecycle
const debugLog = (message, id) => {
  console.log(`[WebSocket ${id}] ${message}`);
};

export const connectSocket = (token) => {
  // Generate a unique connection ID for this attempt
  const currentConnectionId = ++connectionId;

  // Check if socket is already connected
  if (socket && socket.readyState === WebSocket.OPEN) {
    debugLog(
      `Connection already exists and is open. Reusing existing connection.`,
      currentConnectionId
    );
    return socket;
  }

  // Check if connection is already in progress
  if (connectionInProgress) {
    debugLog(
      `Connection already in progress. Waiting for it to complete.`,
      currentConnectionId
    );
    return socket;
  }

  debugLog(`Starting new connection attempt`, currentConnectionId);
  connectionInProgress = true;

  // Close any existing socket that might be in CONNECTING or CLOSING state
  if (socket && socket.readyState !== WebSocket.CLOSED) {
    debugLog(
      `Closing existing socket in state ${socket.readyState}`,
      currentConnectionId
    );
    socket.close();
  }

  // Create new WebSocket connection
  try {
    debugLog(`Creating new WebSocket`, currentConnectionId);
    socket = new WebSocket(`wss://apizeus.slapie.com.br/ws/chat/?jwt=${token}`);

    socket.onopen = () => {
      debugLog(`Connection established`, currentConnectionId);
      connectionInProgress = false;
      retryCount = 0;
      heartbeatCount = 0;
      lastHeartbeatTime = Date.now();

      // Start checking for missed heartbeats (every 30 seconds)
      if (heartbeatCheckInterval) {
        clearInterval(heartbeatCheckInterval);
      }

      heartbeatCheckInterval = setInterval(() => {
        const now = Date.now();
        // If no heartbeat received in 45 seconds (3x the expected 15-second interval)
        if (now - lastHeartbeatTime > 45000) {
          debugLog(
            `No heartbeat received in 45 seconds, connection may be dead`,
            currentConnectionId
          );
          // Force close and trigger reconnect
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.close();
          }
        }
      }, 30000);
    };

    socket.onmessage = (event) => {
      // Check if the data is a Blob and handle it accordingly
      if (event.data instanceof Blob) {
        // Read the blob as text
        event.data
          .text()
          .then((text) => {
            try {
              const message = JSON.parse(text);
              handleMessage(message);
            } catch (error) {
              console.error("Error parsing message:", error, text);
            }
          })
          .catch((error) => {
            console.error("Error reading blob as text:", error);
          });
      } else {
        // Handle text data directly
        try {
          const message = JSON.parse(event.data);
          handleMessage(message);
        } catch (error) {
          console.error("Error parsing message:", error);
        }
      }
    };

    socket.onerror = (error) => {
      debugLog(
        `Error occurred: ${error.message || "Unknown error"}`,
        currentConnectionId
      );
      connectionInProgress = false;
    };

    socket.onclose = (event) => {
      debugLog(
        `Connection closed: code=${event.code}, reason=${event.reason}`,
        currentConnectionId
      );
      connectionInProgress = false;

      // Clear heartbeat check interval
      if (heartbeatCheckInterval) {
        clearInterval(heartbeatCheckInterval);
        heartbeatCheckInterval = null;
      }

      // Only attempt reconnect if this is still the current socket
      if (retryCount < maxRetries) {
        const delay = 1000; // Use a longer delay (1 second) to prevent rapid reconnection attempts
        debugLog(
          `Will attempt reconnect in ${delay}ms (attempt ${
            retryCount + 1
          }/${maxRetries})`,
          currentConnectionId
        );

        setTimeout(() => {
          // Only reconnect if no new connection has been established
          if (!socket || socket.readyState !== WebSocket.OPEN) {
            retryCount += 1;
            connectSocket(token); // Attempt to reconnect
          } else {
            debugLog(
              `Reconnect cancelled, new connection already established`,
              currentConnectionId
            );
          }
        }, delay);
      } else {
        debugLog(
          `Max retries reached. Giving up on WebSocket reconnection.`,
          currentConnectionId
        );
      }
    };

    return socket;
  } catch (error) {
    debugLog(
      `Error creating WebSocket: ${error.message || "Unknown error"}`,
      currentConnectionId
    );
    connectionInProgress = false;
    return null;
  }
};

// Returns the current socket if it exists and is open
export const getSocket = () => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    return socket;
  }
  return null;
};

export const sendCustomMessage = (messagePayload) => {
  const activeSocket = getSocket();
  if (activeSocket) {
    activeSocket.send(JSON.stringify(messagePayload));
  } else {
    console.error("Socket is not open. Cannot send message.");
  }
};

// Register a listener function to be called on new messages
export const setMessageListener = (listener) => {
  messageListener = listener;
};

export const disconnectSocket = () => {
  if (socket) {
    const currentReadyState = socket.readyState;

    // Clear heartbeat check interval
    if (heartbeatCheckInterval) {
      clearInterval(heartbeatCheckInterval);
      heartbeatCheckInterval = null;
    }

    // Reset connection flags
    connectionInProgress = false;

    // Close the socket if it's not already closed
    if (currentReadyState !== WebSocket.CLOSED) {
      socket.close();
      console.log(`WebSocket disconnected (was in state ${currentReadyState})`);
    }

    // Reset all variables
    socket = null;
    messageListener = null;
    retryCount = 0;
    heartbeatCount = 0;

    return true;
  }
  return false;
};

function handleMessage(message) {
  // Handle heartbeat messages from server
  if (message.type === "heartbeat") {
    // Update the last heartbeat time
    lastHeartbeatTime = Date.now();

    // Respond with an empty message to acknowledge the heartbeat
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send("");
    }

    // Log only the first few heartbeats to avoid console spam
    heartbeatCount++;
    if (heartbeatCount <= 3) {
      console.log(
        `Received heartbeat #${heartbeatCount}, timestamp: ${message.timestamp}`
      );
    }

    // Don't process heartbeats further or notify listeners
    return;
  }

  if (message.type === "order_update") {
    const isTracked = Boolean(
      store.getState().orders.orders.entities[message.order_id]
    );
    // Same "still relevant" rule updateOrderStatus uses to decide removal —
    // if we've never seen this order (e.g. a SCHEDULED order that got
    // evicted by a full refresh before its preparation_start arrived, see
    // get_base_scheduled_orders_filter) and its status isn't one we'd drop
    // anyway, fetch it fresh instead of silently ignoring the update.
    const shouldRemove = message.new_status > 2 && message.new_status !== 7;

    if (!isTracked && !shouldRemove) {
      getOrdersV2({ oid: message.order_id })
        .then(({ data }) => {
          if (data) {
            store.dispatch(addCreatedOrders(data));
          }
        })
        .catch(() => {});
    } else {
      store.dispatch(
        updateOrderStatus({
          order_id: message.order_id,
          new_status: message.new_status,
        })
      );
    }

    if (message.new_status === ORDER_STATUS_CANCELLED) {
      store.dispatch(addCancelledOrderToCache(message.order_id));
      dispatchAddAlert({
        label: `CANCELADO ${message.order_index}`,
        message: `Pedido CANCELADO ${message.order_index}`,
        order_index: message.order_index,
        order_id: message.order_id,
        customer_id: message.customer_id,
        type: "order-cancelled",
      });
    }
  }

  if (message.type === "orders_created") {
    store.dispatch(addCreatedOrders(message.message));
    refreshCreatedOrder(message.message);
  }

  if (message.type === "shipping_route_update") {
    store.dispatch(updateShippingRoutes(message));
  }

  if (messageListener) {
    messageListener(message);
  }
}
